import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FormikProps } from 'formik'
import { DateTime } from 'luxon'

import { actions, selectors } from '../../redux'
import { useSagaTakeEvery } from '../../hooks/useSaga'
import { Screen } from '../../types/global'
import { useDefaultLayoutProps } from '../../layouts/DefaultLayout/useProps'
import { groupSlotsByDay } from '../../helpers/SlotsHelpers'
import { RegisterFormValues } from '../../components/forms/RegisterForm'
import { isSameDay } from '../../helpers/DateHelpers'

import { RegisterTemplateProps } from './index'

export const useRegisterTemplateProps = (): RegisterTemplateProps => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const locale = useSelector(selectors.app.currentLocale)
  const registerFormValues = useSelector(selectors.app.registerFormValues)
  const getEventBySlug = useSelector(selectors.app.getEventBySlug)
  const sendVerificationCodes = useSelector(selectors.app.sendVerificationCodes)
  const register = useSelector(selectors.app.register)
  const selectedSlotId = useSelector(selectors.app.selectedSlotId)
  const registerCode = useSelector(selectors.app.registerCode)

  const layoutProps = useDefaultLayoutProps()

  const [hasAttendant, setHasAttendant] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>()
  const [selectedSlot, setSelectedSlot] = useState<string>()
  const [formErrors, setFormErrors] = useState<string[]>()

  const $formikRef = useRef<FormikProps<RegisterFormValues>>(null)

  const availabaleDays = useMemo(() => groupSlotsByDay(getEventBySlug.data?.slots ?? []), [
    getEventBySlug.data?.slots,
  ])

  const slots = useMemo(
    () =>
      availabaleDays.find((day) => {
        const date = new Date(day.date)
        return isSameDay(date, selectedDate)
      })?.slots,
    [availabaleDays, selectedDate]
  )

  useSagaTakeEvery(
    actions.app.sendVerificationCodesSuccess,
    useCallback(() => {
      dispatch(actions.app.setSelectedSlotId(selectedSlot!))
      dispatch(actions.app.setScreen(Screen.confirmation))
    }, [dispatch, selectedSlot])
  )

  useSagaTakeEvery(
    actions.app.registerSuccess,
    useCallback(() => {
      dispatch(actions.app.setSelectedSlotId(selectedSlot!))
      dispatch(actions.app.setScreen(Screen.success))
    }, [dispatch, selectedSlot])
  )

  useEffect(() => {
    if (hasAttendant && selectedSlot) {
      const slot = slots?.find((slot) => slot.id === selectedSlot)

      if (slot && slot.remaining_capacity < 2) {
        // If the selected slot has no capacity for the attendant, reset the selected slot
        setSelectedSlot(undefined)
      }
    }
  }, [hasAttendant, selectedSlot, slots])

  useEffect(() => {
    if (registerFormValues?.attendant?.firstName) {
      setHasAttendant(true)
    }
  }, [registerCode, registerFormValues])

  // Helper to format time as "11h00"
  const formatHour = useCallback(
    (value: string) => {
      const dt = DateTime.fromSQL(value, { zone: 'Europe/Paris', locale })
      return dt.isValid ? dt.toFormat("HH'h'mm") : value
    },
    [locale]
  )

  const emailRegex = /^(?!.*\.\.)[a-zA-Z0-9](?:[a-zA-Z0-9._-]{0,62}[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('register.form.validation.firstName')),
    lastName: Yup.string().required(t('register.form.validation.lastName')),
    email: Yup.string()
      .required(t('register.form.validation.email'))
      .matches(emailRegex, t('register.form.validation.email')),
    terms: Yup.bool().oneOf([true], t('register.form.validation.terms')),
    opt: Yup.bool().oneOf([true, false]),
    ...(hasAttendant && {
      attendant: Yup.object().shape({
        firstName: Yup.string().required(t('register.form.validation.attendant.firstName')),
        lastName: Yup.string().required(t('register.form.validation.attendant.lastName')),
        email: Yup.string()
          .required(t('register.form.validation.attendant.email'))
          .matches(emailRegex, t('register.form.validation.attendant.email.invalid'))
          .test(
            'attendant-email-not-same',
            t('register.form.validation.attendant.notSameAsMain'),
            function (value) {
              const email = $formikRef.current?.values.email
              if (!hasAttendant) return true
              if (!value || !email) return true
              return value !== email
            }
          ),
      }),
    }),
  })

  const handleValidate = useCallback(
    (values: any) => {
      try {
        validationSchema.validateSync(values, { abortEarly: false })
        return {} // No errors
      } catch (err) {
        const errors: { [key: string]: string } = {}
        if (err instanceof Yup.ValidationError && err.inner) {
          err.inner.forEach((e) => {
            if (e.path) errors[e.path] = e.message
          })
        }
        setFormErrors(Object.values(errors))
        return errors
      }
    },
    [validationSchema]
  )

  // WHEN SELECTED DATE CHANGE, RESET SELECTED SLOT
  useEffect(() => {
    if (selectedDate) {
      setSelectedSlot(undefined)
    }
  }, [selectedDate])

  return useMemo(
    () => ({
      layout: layoutProps,
      titleDescription: {
        title: getEventBySlug.data?.frontTitle ?? '',
        description: getEventBySlug.data?.description,
        image: { src: getEventBySlug.data?.frontImage ?? '', alt: 'cover' },
        imageDescription: getEventBySlug.data?.frontDescription ?? '',
        isCentered: true,
      },
      registerForm: {
        formik: {
          innerRef: $formikRef,
          initialValues: {
            firstName: registerFormValues?.firstName ?? '',
            lastName: registerFormValues?.lastName ?? '',
            email: registerFormValues?.email ?? '',
            terms: registerFormValues?.terms ?? false,
            opt: registerFormValues?.opt ?? false,
            attendant: {
              firstName: registerFormValues?.attendant?.firstName ?? '',
              lastName: registerFormValues?.attendant?.lastName ?? '',
              email: registerFormValues?.attendant?.email ?? '',
            },
          },
          validate: handleValidate,
          onSubmit: (values) => {
            if (!registerCode && sendVerificationCodes.success) {
              dispatch(actions.app.setScreen(Screen.confirmation))
            } else if (!registerCode && !sendVerificationCodes.success) {
              dispatch(
                actions.app.setRegisterFormValues({
                  ...values,
                  attendant: undefined,
                  ...(hasAttendant && {
                    attendant: values.attendant,
                  }),
                })
              )
              dispatch(
                actions.app.sendVerificationCodesRequest({
                  firstName: values.firstName,
                  event: getEventBySlug.data!.uuid,
                  email: values.email,
                })
              )
            } else if (registerCode) {
              dispatch(
                actions.app.registerRequest({
                  emailCode: registerCode!,
                  event: getEventBySlug.data!.uuid!,
                  eventSlot: selectedSlot!,
                  optinRegistration: values.opt,
                  visitors: [
                    {
                      first_name: values!.firstName,
                      last_name: values!.lastName,
                      email: values!.email,
                    },
                    ...(hasAttendant
                      ? [
                          {
                            first_name: values.attendant!.firstName,
                            last_name: values.attendant!.lastName,
                            email: values.attendant!.email,
                          },
                        ]
                      : []),
                  ],
                })
              )
            }
          },
        },
        fields: {
          firstName: {
            name: 'firstName',
            label: t('register.form.firstName.label'),
            required: true,
          },
          lastName: {
            name: 'lastName',
            label: t('register.form.lastName.label'),
            required: true,
          },
          email: {
            name: 'email',
            label: t('register.form.email.label'),
            disabled: !!selectedSlotId,
            required: true,
          },
          terms: {
            name: 'terms',
            label: t('register.form.terms.label'),
            required: true,
          },
          opt: {
            name: 'opt',
            label: t('register.form.opt.label'),
          },
          ...(hasAttendant && {
            attendant: {
              firstName: {
                name: 'attendant.firstName',
                label: t('register.form.firstName.label'),
                required: true,
              },
              lastName: {
                name: 'attendant.lastName',
                label: t('register.form.lastName.label'),
                required: true,
              },
              email: {
                name: 'attendant.email',
                label: t('register.form.email.label'),
                required: true,
              },
            },
          }),
        },
        attendant: {
          title: t('register.attendant.title'),
          onDelete: () => {
            if ($formikRef.current) {
              $formikRef.current.setFieldValue('attendant.firstName', '')
              $formikRef.current.setFieldValue('attendant.lastName', '')
              $formikRef.current.setFieldValue('attendant.email', '')
            }
            setHasAttendant(false)
          },
          button: {
            text: t('register.attendant.button.text'),
            onClick: () => setHasAttendant(true),
          },
        },
      },
      calendar: {
        title: t('register.calendar.title'),
        cells: availabaleDays.map((day) => ({ date: new Date(day.date), type: 'available' })),
        selectedDate,
        onSelect: (date) => setSelectedDate(date),
      },
      ...(selectedDate &&
        slots && {
          slots: {
            title: t('register.slots.title'),
            items: slots.map((slot) => ({
              id: slot.id,
              disabled: hasAttendant
                ? slot.remaining_capacity < 2
                : slot.remaining_capacity === 0 || !slot.bookable,
              selected: selectedSlot === slot.id,
              label: `${formatHour(slot.opening_hour)} - ${formatHour(slot.closing_hour)}`,
            })),
            onChange: (item) => {
              setSelectedSlot(item.id)
            },
          },
        }),
      errors: registerCode
        ? register.errors
        : sendVerificationCodes.errors
        ? sendVerificationCodes.errors
        : formErrors?.map((error) => ({ label: error })),
      submitButton: {
        text: t('register.form.submitButton'),
        disabled: !selectedSlot || register.pending || sendVerificationCodes.pending,
        onClick: () => $formikRef.current?.submitForm(),
      },
    }),
    [
      dispatch,
      selectedDate,
      getEventBySlug.data,
      layoutProps,
      registerFormValues,
      register.pending,
      sendVerificationCodes.pending,
      sendVerificationCodes.errors,
      hasAttendant,
      availabaleDays,
      selectedSlot,
      registerCode,
      selectedSlotId,
      handleValidate,
      t,
      slots,
      formatHour,
      formErrors,
      register.errors,
      sendVerificationCodes.success,
    ]
  )
}
